import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { Checkbox, SegmentedControl } from '@column/column-ui-kit';

import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { formatTransferType } from '~/lib/transfers/utils';
import { TransferTypeOptions, UserTransferFilterSnapshot } from '~/repositories';
import { TransferBatchTypeOptions } from '~/repositories/Transfer/TransferRepository';
import { formatString } from '~/util';

const SelectOptionWrapper = styled.div`
  display: grid;
  grid-template-columns: auto;
  background-color: ${({ theme }) => theme.background};
  border-radius: ${({ theme }) => theme.style.borderRadiusM};
  box-shadow:
    inset 0 -0.5px 1.5px ${({ theme }) => theme.secondary.blendToBackground(150)},
    inset 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(150)},
    0 1px 2px ${({ theme }) => theme.secondary.blendToBackground(150)},
    0 3px 10px -2px ${({ theme }) => theme.secondary.blendToBackground(125)};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SelectOption = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};

  * {
    pointer-events: none;
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    &:nth-child(even) {
      border-left: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
    }

    &:last-child,
    &:nth-last-child(2) {
      border-bottom: none;
    }
  }
`;

export const TransferTypeFilter: FC = () => {
  const { watch, setValue } = useFormContext<Partial<UserTransferFilterSnapshot>>();

  const cryptoEnabled = useFeatureFlag(FeatureFlag.EnableLiquidationRouteAPIs);
  const options = useMemo(
    () => TransferTypeOptions.filter((option) => option !== 'crypto' || cryptoEnabled),
    [cryptoEnabled]
  );

  const isSelectOptionActive = useCallback(
    (key: string) => {
      const currentType = watch('filters.type');

      if (!currentType || typeof currentType !== 'string') {
        return false;
      }

      return currentType.split(',').includes(key);
    },
    [watch]
  );

  const handleOptionChange = useCallback(
    (key: string) => {
      const currentType = watch('filters.type');

      const active = currentType && typeof currentType === 'string' ? currentType.split(',') : [];

      if (isSelectOptionActive(key)) {
        setValue('filters.type', active.filter((s) => s !== key).join(','));
      } else {
        setValue('filters.type', [...active, key].join(','));
      }
    },
    [watch, setValue]
  );

  const hasBatchType = TransferBatchTypeOptions.some((entry) => isSelectOptionActive(entry));

  useEffect(() => {
    if (hasBatchType) {
      setValue('filters.isIncoming', null);
    }
  }, [hasBatchType, setValue]);

  return (
    <>
      <SegmentedControl
        isDisabled={hasBatchType}
        options={[
          {
            label: 'All',
            value: null,
          },
          {
            label: 'Incoming',
            value: true,
          },
          {
            label: 'Outgoing',
            value: false,
          },
        ]}
        fullWidth
        activeUndefined
        active={hasBatchType ? null : watch('filters.isIncoming')}
        onOptionChange={(value) => setValue('filters.isIncoming', value)}
      />

      <SelectOptionWrapper>
        {options.map((entry) => (
          <SelectOption key={entry} onClick={() => handleOptionChange(entry)}>
            <Checkbox isChecked={isSelectOptionActive(entry)} />
            {formatString(entry)
              .replaceAll('Ach', 'ACH')
              .replaceAll('Swift', 'International Wire')
              .replaceAll('Check credit', 'Deposited Check')
              .replaceAll('Check debit', 'Issued Check')
              .replaceAll('Crypto', 'Stablecoin')}
          </SelectOption>
        ))}
        {TransferBatchTypeOptions.map((entry) => (
          <SelectOption key={entry} onClick={() => handleOptionChange(entry)}>
            <Checkbox isChecked={isSelectOptionActive(entry)} />
            {formatTransferType(entry)}
          </SelectOption>
        ))}
      </SelectOptionWrapper>
    </>
  );
};
